import { LinkOutlined, MessageOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, FloatButton, Popconfirm, Space, Table, Typography } from "antd";
import message from "../../message";
import KnowledgeChatModal from "./KnowledgeChatModal";
import KnowledgeCrawlModal from "./KnowledgeCrawlModal";
import KnowledgeModal from "./KnowledgeModal";

const { Paragraph } = Typography;

export default function KnowledgePage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [crawlOpen, setCrawlOpen] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [crawlLogs, setCrawlLogs] = useState([]);
  const [saveStatusText, setSaveStatusText] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const crawlSourceRef = useRef(null);
  const saveTotalRef = useRef(0);
  const savedCountRef = useRef(0);
  const pendingSaveCountRef = useRef(0);
  const crawlFinishedRef = useRef(false);
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080";
  const token = useMemo(() => localStorage.getItem("token"), []);

  const closeCrawlSource = () => {
    if (!crawlSourceRef.current) return;
    crawlSourceRef.current.close();
    crawlSourceRef.current = null;
  };

  const loadList = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/manager/knowledge/list`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "加载失败");
        return;
      }
      setList(result?.data?.list || []);
      setSelectedRowKeys((prev) => prev.filter((id) => (result?.data?.list || []).some((item) => item.id === id)));
    } catch (error) {
      message.toast("加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    return () => {
      closeCrawlSource();
    };
  }, []);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  const createKnowledge = async (values) => {
    const params = new URLSearchParams({
      title: values.title,
      content: values.content,
    });
    const resp = await fetch(`${baseUrl}/manager/knowledge/add`, {
      method: "POST",
      headers: token ? { token } : {},
      body: params,
    });
    const result = await resp.json();
    if (result?.code) {
      throw new Error(result?.msg || "保存失败");
    }
    return result;
  };

  const handleSave = async (values) => {
    if (saving) return;
    setSaving(true);
    try {
      if (editing?.id) {
        const params = new URLSearchParams({
          id: editing.id,
          title: values.title,
          content: values.content,
        });
        const resp = await fetch(`${baseUrl}/manager/knowledge/update?${params.toString()}`, {
          headers: token ? { token } : {},
        });
        const result = await resp.json();
        if (result?.code) {
          message.toast(result?.msg || "保存失败");
          return;
        }
      } else {
        await createKnowledge(values);
      }
      message.success("保存成功");
      setModalOpen(false);
      setEditing(null);
      await loadList();
    } catch (error) {
      message.toast("保存失败");
    } finally {
      setSaving(false);
    }
  };

  const appendCrawlLog = (text) => {
    if (!text) return;
    setCrawlLogs((prev) => [...prev, text]);
  };

  const syncSaveStatusText = () => {
    if (pendingSaveCountRef.current > 0) {
      setSaveStatusText(`正在入库 ${savedCountRef.current}/${saveTotalRef.current}`);
      return;
    }
    if (crawlFinishedRef.current && saveTotalRef.current > 0 && savedCountRef.current === saveTotalRef.current) {
      setSaveStatusText("已完成");
      return;
    }
    setSaveStatusText("");
  };

  const resetSaveStatus = () => {
    saveTotalRef.current = 0;
    savedCountRef.current = 0;
    pendingSaveCountRef.current = 0;
    crawlFinishedRef.current = false;
    setSaveStatusText("");
  };

  const handleStopCrawl = () => {
    closeCrawlSource();
    setCrawling(false);
    crawlFinishedRef.current = true;
    syncSaveStatusText();
    appendCrawlLog("已停止采集");
  };

  const handleStartCrawl = async ({ url, apiKey }) => {
    if (crawling) return;

    closeCrawlSource();
    setCrawlLogs([`开始采集：${url}`]);
    setCrawling(true);
    resetSaveStatus();

    try {
      const params = new URLSearchParams({
        mainLink: url.trim(),
        deepseekApiKey: apiKey.trim(),
      });
      const source = new EventSource(`https://api.wadesk.cn/py/crawl?${params.toString()}`);
      crawlSourceRef.current = source;

      source.addEventListener("log", (event) => {
        try {
          const data = JSON.parse(event.data || "{}");
          const logMessage = data?.message || "";
          appendCrawlLog(logMessage);
          if (logMessage === "全部完成") {
            crawlFinishedRef.current = true;
            closeCrawlSource();
            setCrawling(false);
            syncSaveStatusText();
          }
        } catch (error) {
          appendCrawlLog("收到无法解析的日志");
        }
      });

      source.addEventListener("result", async (event) => {
        saveTotalRef.current += 1;
        pendingSaveCountRef.current += 1;
        syncSaveStatusText();
        try {
          const data = JSON.parse(event.data || "{}");
          await createKnowledge({
            title: data?.title || "",
            content: data?.content || "",
          });
          savedCountRef.current += 1;
          await loadList();
        } catch (error) {
          appendCrawlLog(error?.message || "保存失败");
          message.toast(error?.message || "保存失败");
        } finally {
          pendingSaveCountRef.current -= 1;
          syncSaveStatusText();
        }
      });

      source.onerror = () => {
        appendCrawlLog("采集连接异常");
        message.toast("采集失败");
        crawlFinishedRef.current = true;
        closeCrawlSource();
        setCrawling(false);
        syncSaveStatusText();
      };
    } catch (error) {
      appendCrawlLog("采集启动失败");
      message.toast("采集启动失败");
      crawlFinishedRef.current = true;
      closeCrawlSource();
      setCrawling(false);
      syncSaveStatusText();
    }
  };

  const deleteKnowledge = async (id) => {
    const params = new URLSearchParams({ id });
    const resp = await fetch(`${baseUrl}/manager/knowledge/delete?${params.toString()}`, {
      headers: token ? { token } : {},
    });
    const result = await resp.json();
    if (result?.code) {
      throw new Error(result?.msg || "删除失败");
    }
  };

  const handleDelete = async (record) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteKnowledge(record.id);
      message.success("删除成功");
      setList((prev) => prev.filter((item) => item.id !== record.id));
      setSelectedRowKeys((prev) => prev.filter((id) => id !== record.id));
    } catch (error) {
      message.toast(error?.message || "删除失败");
    } finally {
      setDeleting(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0 || deleting) return;
    setDeleting(true);
    try {
      for (const id of selectedRowKeys) {
        await deleteKnowledge(id);
      }
      message.success("批量删除成功");
      setList((prev) => prev.filter((item) => !selectedRowKeys.includes(item.id)));
      setSelectedRowKeys([]);
    } catch (error) {
      message.toast(error?.message || "批量删除失败");
    } finally {
      setDeleting(false);
    }
  };

  const handleAsk = async (question) => {
    const content = question.trim();
    if (!content || chatLoading) return;

    setChatLoading(true);
    setChatMessages((prev) => [...prev, { role: "user", content }]);
    try {
      const params = new URLSearchParams({ question: content });
      const resp = await fetch(`${baseUrl}/manager/knowledge/ask?${params.toString()}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      if (result?.code) {
        message.toast(result?.msg || "发送失败");
        setChatMessages((prev) => prev.slice(0, -1));
        return;
      }
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result?.data?.answer || "None",
        },
      ]);
    } catch (error) {
      message.toast("发送失败");
      setChatMessages((prev) => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const columns = [
    {
      title: "序号",
      key: "index",
      width: 80,
      align: "center",
      render: (_value, _record, index) => index + 1,
    },
    {
      title: "标题",
      dataIndex: "title",
      key: "title",
      width: 240,
      align: "center",
      render: (value) => <span style={{ display: "block", fontWeight: 500, textAlign: "center" }}>{value || "-"}</span>,
    },
    {
      title: "内容",
      dataIndex: "content",
      key: "content",
      align: "center",
      render: (value) => (
        <Paragraph
          ellipsis={{ rows: 3, expandable: "collapsible", symbol: (expanded) => (expanded ? "收起" : "展开") }}
          style={{ marginBottom: 0, whiteSpace: "pre-wrap", textAlign: "center" }}
        >
          {String(value || "-")}
        </Paragraph>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "createTime",
      key: "createTime",
      width: 180,
      align: "center",
      render: (value) => value || "-",
    },
    {
      title: "操作",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该知识？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger loading={deleting}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Space>
          <Button type="primary" onClick={openAdd}>
            +新增知识
          </Button>
          <Button icon={<LinkOutlined />} onClick={() => setCrawlOpen(true)}>
            从链接采集
          </Button>
          <Popconfirm
            title={`确认删除已选中的 ${selectedRowKeys.length} 条知识？`}
            onConfirm={handleBatchDelete}
            disabled={selectedRowKeys.length === 0}
          >
            <Button danger disabled={selectedRowKeys.length === 0} loading={deleting}>
              批量删除
            </Button>
          </Popconfirm>
        </Space>
        <Button onClick={loadList} loading={loading}>
          刷新
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        bordered
        pagination={{ pageSize: 10 }}
        className="manager-knowledge-table"
      />
      <FloatButton
        type="primary"
        icon={<MessageOutlined />}
        tooltip="知识库对话"
        onClick={() => setChatOpen(true)}
      />
      <KnowledgeModal
        open={modalOpen}
        saving={saving}
        record={editing}
        onCancel={() => {
          if (saving) return;
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
      />
      <KnowledgeCrawlModal
        open={crawlOpen}
        crawling={crawling}
        logs={crawlLogs}
        saveStatusText={saveStatusText}
        onCancel={() => {
          if (crawling) return;
          setCrawlOpen(false);
        }}
        onStart={handleStartCrawl}
        onStop={handleStopCrawl}
      />
      <KnowledgeChatModal
        open={chatOpen}
        loading={chatLoading}
        messages={chatMessages}
        onCancel={() => {
          if (chatLoading) return;
          setChatOpen(false);
        }}
        onSubmit={handleAsk}
        onClear={() => setChatMessages([])}
      />
    </div>
  );
}
